import React, { useState } from 'react';
import { Buildings, House, ShieldCheck, DotsThreeVertical, Link as LinkIcon, Copy, Check, PencilSimple, Eye, SealCheck, Star, Plus } from '@phosphor-icons/react';
import { ActionMenu } from '../ui/ActionMenu';
import { Session, updateSession, getBookmarks, getSession, getOffersFromSupply, getMyProperties, togglePropertyActive, deleteProperty, getRentedProperties, SavedProperty, getStoredReviewsForSubject } from '../../lib/utils';
import { LandlordRentalHistory, LandlordRentalEntry } from './LandlordRentalHistory';
import { EditLandlordProfileModal, LandlordEditData, LandlordEditFocusSection } from '../forms/EditLandlordProfileModal';
import { LandlordIdentityCard, LandlordIdentityData } from './LandlordIdentityCard';
import { VerifyEmailModal } from '../forms/VerifyEmailModal';
import { VerifyIdentityModal } from '../forms/VerifyIdentityModal';
import { RequestReviewModal } from '../forms/RequestReviewModal';
import { ReviewCard, Review, ReviewsRatingSummary } from './ReviewCard';
import { PendingReviewRequest, ReviewRequest } from './PendingReviewRequest';
import { MyPropertiesList } from './MyPropertiesList';
import { PAGE_ROUTE } from '../../lib/routes';
interface SupplyProfileViewProps {
  session: Session;
  navigate: (page: string, context?: any) => void;
}
export function SupplyProfileView({
  session,
  navigate
}: SupplyProfileViewProps) {
  // Local mirror of session so mutations re-render instantly.
  const [liveSession, setLiveSession] = useState<Session>(() => getSession() || session);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editFocusSection, setEditFocusSection] = useState<LandlordEditFocusSection | undefined>(undefined);
  const [verifyModal, setVerifyModal] = useState<'email' | 'identity' | 'business' | null>(null);
  const [requestModalOpen, setRequestModalOpen] = useState(false);
  const [reviewRequests, setReviewRequests] = useState<ReviewRequest[]>([]);
  // Supplies see their own reviews here — seeded from the LandlordProfile mock
  // (when one exists for their slug) plus any reviews submitted to them through
  // the ReviewSubmission flow.
  const [reviews, setReviews] = useState<Review[]>(() => {
    const slug = session.slug;
    if (!slug) return [];
    return getStoredReviewsForSubject(slug).map((r): Review => ({
      id: r.id,
      reviewerName: r.reviewerName,
      reviewerRole: r.reviewerRole as any,
      reviewerSlug: r.reviewerSlug,
      reviewerVerified: r.reviewerVerified,
      avatar: r.reviewerAvatar,
      rating: r.rating,
      categoryRatings: r.categoryRatings,
      date: r.date,
      body: r.body,
      periodLabel: '',
      status: r.status
    }));
  });
  const [verifications, setVerifications] = useState({
    email: false,
    identity: false,
    business: false
  });
  const [copied, setCopied] = useState(false);
  const [showToast, setShowToast] = useState<string | null>(null);
  const [properties, setProperties] = useState<SavedProperty[]>(() => getMyProperties(getSession()?.email));
  const firstName = liveSession.name?.split(' ')[0] || 'Proprietar';
  const lastName = liveSession.name?.split(' ').slice(1).join(' ') || '';
  const hasSubRole = !!liveSession.subRole;
  const memberSince = liveSession.memberSince || new Date().getFullYear();
  // L8.3 — real stats from the offer store (replaces hard-coded mocks).
  const bookmarksCount = getBookmarks().length;
  const myOffers = liveSession.email ? getOffersFromSupply(liveSession.email) : [];
  const oferteTrimise = myOffers.length;
  const succesOffers = myOffers.filter(o => o.status === 'succes');
  const rentedProperties = liveSession.email ? getRentedProperties(liveSession.email) : [];
  // Prefer the property-based rental store (authoritative). Fall back to
  // succes offers when there are no rented properties yet so mock/demo
  // data still renders a meaningful history.
  const rentalEntries: LandlordRentalEntry[] = rentedProperties.length > 0 ? rentedProperties.map(p => ({
    id: p.id,
    tenantName: p.rentedTenantName || 'Chiriaș',
    listingTitle: p.title || (p as any).listingTitle || (p as any).name || '',
    closedAt: p.rentedAt || p.createdAt || new Date().toISOString()
  })) : succesOffers.map(o => ({
    id: o.id,
    tenantName: o.toName,
    listingTitle: o.listingPreview?.title,
    closedAt: o.succesAt || o.updatedAt
  }));
  const handleSubRoleChoice = (choice: 'proprietar' | 'agent') => {
    updateSession({
      subRole: choice
    });
    setLiveSession(prev => ({
      ...prev,
      subRole: choice
    }));
  };
  const profileSlug = liveSession.slug || liveSession.email?.split('@')[0] || 'proprietar';
  const profileUrl = typeof window !== 'undefined' ? `${window.location.origin}/proprietar/${profileSlug}` : `/proprietar/${profileSlug}`;
  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(profileUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // ignore
    }
  };
  const handleViewPublic = () => {
    navigate(PAGE_ROUTE.LANDLORD_PROFILE, {
      slug: profileSlug,
      viewerIsOwner: true
    });
  };
  const openEdit = (section?: LandlordEditFocusSection) => {
    setEditFocusSection(section);
    setEditModalOpen(true);
  };
  const closeEdit = () => {
    setEditModalOpen(false);
    setEditFocusSection(undefined);
  };
  const handleSaveProfile = (data: LandlordEditData) => {
    const patch: Partial<Session> = {
      avatar: data.avatar,
      name: `${data.firstName} ${data.lastName}`.trim(),
      subRole: data.subRole || undefined,
      agencyName: data.agencyName,
      phone: data.phone,
      website: data.website,
      linkedinUrl: data.linkedinUrl,
      facebookUrl: data.facebookUrl,
      bio: data.bio
    };
    updateSession(patch);
    setLiveSession(prev => ({
      ...prev,
      ...patch
    }));
    closeEdit();
  };
  const markVerified = (flag: 'email' | 'identity' | 'business') => {
    setVerifications(prev => ({
      ...prev,
      [flag]: true
    }));
  };
  const handleVerificationClick = (type: 'email' | 'identity' | 'business') => {
    if (type === 'business') {
      setShowToast(liveSession.subRole === 'agent' ? 'Verificarea agenției va fi disponibilă în curând' : 'Verificarea proprietății va fi disponibilă în curând');
      setTimeout(() => setShowToast(null), 2400);
      return;
    }
    setVerifyModal(type);
  };
  const inchirieriDeSuccess = succesOffers.length;
  const allVerified = verifications.email && verifications.identity && verifications.business;
  const identityCardData: LandlordIdentityData = {
    name: liveSession.name || '',
    avatar: liveSession.avatar,
    subRole: liveSession.subRole || null,
    agencyName: liveSession.agencyName,
    memberSince,
    linkedinUrl: liveSession.linkedinUrl,
    facebookUrl: liveSession.facebookUrl,
    websiteUrl: liveSession.website,
    bio: liveSession.bio,
    verifications,
    stats: {
      oferteTrimise,
      inchirieriDeSuccess
    }
  };
  const handleTogglePropertyActive = (id: string) => {
    togglePropertyActive(id);
    setProperties(getMyProperties(liveSession.email));
  };
  const handleDeletePropertyEntry = (id: string) => {
    deleteProperty(id);
    setProperties(getMyProperties(liveSession.email));
    setShowToast('Proprietatea a fost ștearsă');
    setTimeout(() => setShowToast(null), 2400);
  };
  const handleEditPropertyEntry = (id: string) => {
    // PropertyWizard uses `mode` for 'standalone' | 'offer' (the high-level
    // intent) and `subMode` for 'create' | 'edit'. We're always in standalone
    // mode here; we only differ in subMode.
    navigate(PAGE_ROUTE.PROPERTY_EDITOR, {
      mode: 'standalone',
      subMode: 'edit',
      propertyId: id
    });
  };
  const handleAddPropertyManually = () => {
    navigate(PAGE_ROUTE.PROPERTY_EDITOR, {
      mode: 'standalone',
      subMode: 'create'
    });
  };
  return <>
      <div className="space-y-6" data-id="element-973">
        {/* Page heading with ActionMenu */}
        <div className="flex items-center justify-between gap-3" data-id="element-974">
          <h1 className="text-2xl font-bold text-light-1" data-id="element-975">Profil</h1>
          <ActionMenu triggerAriaLabel="Acțiuni profil" items={[{
          label: 'Editează profil',
          icon: <PencilSimple size={18} weight="regular" data-id="element-977" />,
          onClick: () => openEdit()
        }, {
          label: copied ? 'Link copiat' : 'Copiază link',
          icon: copied ? <Check size={18} weight="bold" data-id="element-978" /> : <LinkIcon size={18} weight="regular" data-id="element-979" />,
          onClick: handleCopyLink,
          closeOnClick: false,
          rightSlot: copied ? <Check size={14} weight="bold" className="text-lime-1" data-id="element-980" /> : undefined
        }, {
          label: 'Vezi cum apari',
          icon: <Eye size={18} weight="regular" data-id="element-981" />,
          onClick: handleViewPublic
        }, {
          label: 'Verificări',
          icon: <SealCheck size={18} weight="regular" data-id="element-982" />,
          onClick: () => {
            const el = document.getElementById('profile-verifications');
            if (el) {
              el.scrollIntoView({
                behavior: 'smooth',
                block: 'start'
              });
            }
          }
        }]} data-id="element-976" />
        </div>

        {/* Sub-role nudge — only if not set */}
        {!hasSubRole && <div className="bg-dark-2 border border-lime-4 rounded-2xl p-6" data-id="element-983">
            <h3 className="text-base font-semibold text-light-1 mb-1" data-id="element-984">
              Spune-ne ce fel de cont ai
            </h3>
            <p className="text-sm text-light-3 mb-4" data-id="element-985">
              Alege rolul tău ca să-ți personalizăm experiența.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-986">
              <button type="button" onClick={() => handleSubRoleChoice('proprietar')} className="flex items-center gap-3 px-4 py-3 rounded-md bg-dark-3 border border-dark-4 text-light-1 hover:border-lime-4 hover:bg-dark-3/80 transition-colors cursor-pointer" data-id="element-987">
                <House size={20} weight="fill" className="text-lime-1" data-id="element-988" />
                <span className="text-sm font-medium" data-id="element-989">Sunt proprietar</span>
              </button>
              <button type="button" onClick={() => handleSubRoleChoice('agent')} className="flex items-center gap-3 px-4 py-3 rounded-md bg-dark-3 border border-dark-4 text-light-1 hover:border-lime-4 hover:bg-dark-3/80 transition-colors cursor-pointer" data-id="element-990">
                <Buildings size={20} weight="fill" className="text-lime-1" data-id="element-991" />
                <span className="text-sm font-medium" data-id="element-992">
                  Sunt agent imobiliar
                </span>
              </button>
            </div>
          </div>}

        {/* Verification nudge — top, only if not all verified */}
        {!allVerified && <div className="rounded-2xl bg-lime-5 border border-lime-4 p-4 sm:p-5 flex items-start gap-3" data-id="element-993">
            <ShieldCheck size={22} weight="duotone" className="text-lime-1 shrink-0 mt-0.5" data-id="element-994" />
            <div className="flex-1 min-w-0" data-id="element-995">
              <h3 className="text-sm font-semibold text-light-1" data-id="element-996">
                Verifică-ți contul
              </h3>
              <p className="text-xs sm:text-sm text-light-2 mt-0.5 leading-relaxed" data-id="element-997">
                Conturile verificate primesc de 3× mai multe răspunsuri.
              </p>
              <button type="button" onClick={() => {
            const el = document.getElementById('profile-verifications');
            if (el) {
              el.scrollIntoView({
                behavior: 'smooth',
                block: 'start'
              });
            }
          }} className="mt-2.5 text-xs font-medium text-lime-1 hover:text-lime-2 cursor-pointer" data-id="element-998">
                Începe verificarea →
              </button>
            </div>
          </div>}

        {/* Landlord Identity Card */}
        <LandlordIdentityCard data={identityCardData} mode="owner" onOpenEditProfile={openEdit} onOpenVerification={handleVerificationClick} data-id="element-999" />

        {/* REVIEWS CARD */}
        <div className="rounded-2xl bg-dark-2 border border-dark-3 px-6 py-7" data-id="element-1000">
          <div className="flex items-center justify-between mb-4" data-id="element-1001">
            <h3 className="text-base font-semibold text-light-1" data-id="element-1002">Recenzii</h3>
            <ReviewsRatingSummary reviews={reviews} data-id="element-1003" />
          </div>

          {reviews.length === 0 && reviewRequests.length === 0 ? <div className="rounded-xl border border-dashed border-dark-4 p-5 text-center" data-id="element-1004">
              <Star size={36} weight="regular" className="text-light-4 mx-auto mb-3" data-id="element-1005" />
              <p className="text-sm text-light-3 leading-relaxed mb-3" data-id="element-1006">
                Nicio recenzie încă. Cere recenzii de la foști chiriași ca să
                crești încrederea în profilul tău.
              </p>
              <button onClick={() => setRequestModalOpen(true)} className="inline-flex items-center gap-1.5 h-9 px-4 rounded-lg bg-dark-3 hover:bg-dark-4 text-light-1 text-xs font-medium border border-dark-4 transition-colors cursor-pointer" data-id="element-1007">
                <Plus size={14} weight="bold" data-id="element-1008" />
                Cere o recenzie
              </button>
            </div> : <div className="space-y-4" data-id="element-1009">
              {reviews.map(r => <ReviewCard key={r.id} review={r} onReviewerClick={slug => navigate(PAGE_ROUTE.TENANT_PROFILE, {
            slug
          })} data-id="element-1010" />)}

              {reviewRequests.length > 0 && <div className="space-y-2" data-id="element-1011">
                  <h4 className="text-xs font-medium text-light-3 uppercase tracking-wider mt-6 mb-3" data-id="element-1012">
                    Invitații trimise
                  </h4>
                  {reviewRequests.map(req => <PendingReviewRequest key={req.id} request={req} onResend={id => {
              setReviewRequests(prev => prev.map(r => r.id === id ? {
                ...r,
                sentAt: 'Acum'
              } : r));
            }} onCancel={id => {
              setReviewRequests(prev => prev.filter(r => r.id !== id));
            }} onTestLink={token => {
              navigate(PAGE_ROUTE.REVIEW_SUBMISSION, {
                token
              });
            }} data-id="element-1013" />)}
                </div>}

              <div className="pt-4" data-id="element-1014">
                <button onClick={() => setRequestModalOpen(true)} className="inline-flex items-center gap-1.5 h-9 px-4 rounded-lg bg-dark-3 hover:bg-dark-4 text-light-1 text-xs font-medium border border-dark-4 transition-colors cursor-pointer" data-id="element-1015">
                  <Plus size={14} weight="bold" data-id="element-1016" />
                  Cere altă recenzie
                </button>
              </div>
            </div>}
        </div>

        {/* L8.3 — Landlord rental history (real data from the offer store) */}
        <LandlordRentalHistory entries={rentalEntries} isOwner subRole={liveSession.subRole || null} onViewArchive={() => navigate(PAGE_ROUTE.DASHBOARD_APLICARI_ARCHIVE)} data-id="element-1017" />

        {/* L9 — Proprietățile mele */}
        <MyPropertiesList properties={properties} onToggleActive={handleTogglePropertyActive} onEdit={handleEditPropertyEntry} onDelete={handleDeletePropertyEntry} onAddManual={handleAddPropertyManually} onGoToMarketplace={() => navigate(PAGE_ROUTE.MARKETPLACE)} data-id="element-1018" />
      </div>

      {/* Toast notification */}
      {showToast && <div className="fixed bottom-20 left-1/2 -translate-x-1/2 z-50 px-4 py-3 rounded-md bg-dark-2 border border-dark-3 text-sm text-light-1 shadow-2xl" data-id="element-1019">
          {showToast}
        </div>}

      {/* Edit profile modal */}
      <EditLandlordProfileModal open={editModalOpen} focusSection={editFocusSection} initialData={{
      avatar: liveSession.avatar || '',
      firstName,
      lastName,
      subRole: liveSession.subRole || null,
      agencyName: liveSession.agencyName || '',
      phone: liveSession.phone || '',
      website: liveSession.website || '',
      linkedinUrl: liveSession.linkedinUrl || '',
      facebookUrl: liveSession.facebookUrl || '',
      bio: liveSession.bio || ''
    }} onClose={closeEdit} onSave={handleSaveProfile} data-id="element-1020" />

      {/* Verification modals */}
      <VerifyEmailModal open={verifyModal === 'email'} email={liveSession.email || `${firstName.toLowerCase()}@example.com`} onClose={() => setVerifyModal(null)} onSuccess={() => {
      markVerified('email');
      setVerifyModal(null);
    }} data-id="element-1021" />
      <VerifyIdentityModal open={verifyModal === 'identity'} onClose={() => setVerifyModal(null)} onSuccess={() => {
      markVerified('identity');
      setVerifyModal(null);
    }} data-id="element-1022" />

      {/* Request review modal */}
      <RequestReviewModal open={requestModalOpen} mode="supply" navigate={navigate} onClose={() => setRequestModalOpen(false)} onSuccess={req => {
      setReviewRequests(prev => [req, ...prev]);
    }} data-id="element-1023" />
    </>;
}